import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { PagedResponse } from '../generic/paged-response';
import { ParamSerializer } from '../generic/param-serializer';
import { environment } from 'src/environments/environment';
import type { Vehicle, VehicleQueryOpts } from './vehicle';
import type { VehiclesByGroup } from './vehicles-by-group';


@Injectable()
export class VehicleService {

  /** API Url - can be overridden by derived classes */
  protected apiUrl: string;

  public constructor(
    @Inject(HttpClient) protected readonly http: HttpClient,
    @Inject(ParamSerializer) protected readonly paramSerializer: ParamSerializer,
  ) {
    this.apiUrl = `${environment.apiBaseUrl}/core/vehicles`;
  }

  /**
   * Gets list of vehicles for dropdown options
   * @returns Vehicle[]
   */
  public getOptions<V = Vehicle>(): Observable<V[]> {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const req = this.search({ limit: 10000 }) as Observable<PagedResponse<V>>;
    return req.pipe(map((x: PagedResponse<V>) => x.data));
  }

  /**
   * Searches for vehicles
   * @param filters Takes VehicleQueryOpts as filters for request
   * @returns PagedResponse<Vehicle>
   */
  public search<V = Vehicle>(filters: VehicleQueryOpts): Observable<PagedResponse<V>> {
    // todo: revisit this once customer api vehicle dto is more fleshed out.
    filters.include = 'all';
    if (filters.id && !Array.isArray(filters.id)) {
      filters.id = [filters.id];
    }

    const serializedParams = this.paramSerializer.serialize(filters);
    const url = `${this.apiUrl}?${serializedParams}`;

    const req = this.http.get<V[]>(url, { observe: 'response' })
      .pipe(
        map(res => ({
          data: res.body ?? [],
          count: res.body ? res.body.length : 0,
        })),
      );

    return req;
  }

  /**
   * Autocomplete search for vehicles
   * @param filters Takes VehicleQueryOpts as filters for request
   * @returns Vehicle[]
   */
  public autocomplete<V = Vehicle>(filters: VehicleQueryOpts): Observable<V[]> {
    return this.search<V>(filters).pipe(map(x => x.data));
  }

  public getStreams(filters: VehicleQueryOpts): Observable<VehiclesByGroup<Vehicle>[]> {
    filters.network_status = 'connected';
    return this.search(filters).pipe(
      map(result => this.groupVehiclesByGroup(result)),
    );
  }

  /**
   * Groups vehicles by their group, transforming PagedResponse into grouped structure
   * @param vehicleStreams PagedResponse containing vehicle data with cameras property
   * @returns Array of vehicles grouped by group
   */
  protected groupVehiclesByGroup<V extends Vehicle = Vehicle>(vehicleStreams: PagedResponse<V>): VehiclesByGroup<V>[] {
    const groups: Record<string, VehiclesByGroup<V>> = {};

    for (const vs of vehicleStreams.data) {
      const groupId = vs.group.id;

      if (!groups[groupId]) {
        groups[groupId] = {
          group: vs.group,
          vehicles: [],
        };
      }

      this.modifyCameras(vs.cameras);
      groups[groupId].vehicles.push(vs);
    }

    return Object.values(groups);
  }

  protected modifyCameras(cameras: { channel: number; qualities?: string[]; name?: string }[]): void {
    for (const camera of cameras) {
      // If the camera name is the same as the channel, remove it.  So we don't show "CH4 - CH4"
      if (camera.name) {
        const defaultName = `CH${camera.channel}`;
        const defaultName2 = `CHANNEL${camera.channel}`;
        const normalizedName = camera.name.toUpperCase().replaceAll(' ', '');
        if (defaultName === normalizedName || defaultName2 === normalizedName) {
          camera.name = undefined;
        }
      }
      if (camera.name && camera.name.length > 20) {
        camera.name = `${camera.name.substring(0, 20)}...`;
      }
    }
  }

}
